import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Star,
  ShieldCheck,
  Calendar,
  Clock,
  ArrowLeft,
  Sparkles,
  Heart,
  Share2,
  Check,
  ChevronRight,
  AlertCircle,
  CreditCard,
  MessageSquare,
} from 'lucide-react';
import { fetchCompanionById } from '../lib/companionDiscoveryService';
import { Companion, BookingDetails } from '../types';
import { StructuredLocation } from '../types/location';
import { BookingConfirmationModal } from '../components/BookingConfirmationModal';
import { BookingMeetingLocationPicker } from '../components/location/BookingMeetingLocationPicker';
import { PairUpLogo } from '../components/common/PairUpLogo';
import { useAuth } from '../context/AuthContext';
import { isDemoCompanionProfile, getDemoCompanionBookingMessage } from '../data/companionsData';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageSwitcher } from '../components/common/LanguageSwitcher';

export const CompanionDetailPage: React.FC = () => {
  const { companionId } = useParams<{ companionId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser } = useAuth();
  const { language, t } = useLanguage();

  const [companion, setCompanion] = useState<Companion | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeBooking, setActiveBooking] = useState<BookingDetails | null>(null);

  // Booking form state
  const [bookingDate, setBookingDate] = useState(() => {
    return new Date(Date.now() + 86400000).toISOString().split('T')[0];
  });
  const [bookingTime, setBookingTime] = useState('18:00');
  const [durationHours, setDurationHours] = useState(2);
  const [selectedOccasion, setSelectedOccasion] = useState('Dinner Date & Conversation');
  const [meetingLocation, setMeetingLocation] = useState('');
  const [meetingLocationDetails, setMeetingLocationDetails] = useState<StructuredLocation | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [showSampleNotAvailableModal, setShowSampleNotAvailableModal] = useState<boolean>(false);

  useEffect(() => {
    const loadData = async () => {
      if (!companionId) return;
      setIsLoading(true);
      const data = await fetchCompanionById(companionId);
      setCompanion(data);
      if (data) {
        setMeetingLocation(`${data.city} City Center / Verified Public Cafe`);
        if (data.supportedOccasions && data.supportedOccasions.length > 0) {
          setSelectedOccasion(data.supportedOccasions[0]);
        }
      }
      setIsLoading(false);
    };

    loadData();
  }, [companionId]);

  const handleStartBooking = () => {
    if (!companion) return;
    setBookingError(null);

    if (isDemoCompanionProfile(companion)) {
      setBookingError(
        'Not available on this time / date. Not Available for This Booking Time. This companion is a sample profile and cannot accept real bookings. Please choose an available verified companion.'
      );
      setShowSampleNotAvailableModal(true);
      return;
    }

    if (!isAuthenticated && !currentUser) {
      alert('Please log in or sign up first to book a companion.');
      navigate('/login');
      return;
    }

    if (!meetingLocation.trim() && !meetingLocationDetails) {
      setBookingError('Please search and select a meeting location.');
      return;
    }

    const hourlyRate = companion.hourlyRate || 999;
    const baseAmount = hourlyRate * durationHours;
    const taxes = Math.round(baseAmount * 0.18);
    const platformFee = 99;
    const totalAmount = baseAmount + taxes + platformFee;

    const details: BookingDetails = {
      companion,
      date: bookingDate,
      time: bookingTime,
      durationHours,
      occasion: selectedOccasion,
      meetingLocation: meetingLocationDetails?.formatted_address || meetingLocation || `${companion.city} Public Venue`,
      meeting_location_details: meetingLocationDetails || undefined,
      city: meetingLocationDetails?.city || companion.city,
      baseAmount,
      taxes,
      platformFee,
      totalAmount,
    };

    setActiveBooking(details);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-3 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs font-bold text-slate-500">Loading companion profile...</p>
        </div>
      </div>
    );
  }

  if (!companion) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl max-w-md w-full text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-black text-slate-900">Companion Not Found</h2>
          <p className="text-xs text-slate-500">
            We could not find the requested companion host profile.
          </p>
          <div className="pt-2">
            <Link
              to="/home"
              className="px-4 py-2.5 bg-slate-900 text-white font-bold text-xs rounded-xl shadow-xs inline-block"
            >
              Browse Available Companions
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const hourlyRate = companion.hourlyRate || 999;
  const computedTotal = hourlyRate * durationHours;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col selection:bg-pink-500 selection:text-white">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <PairUpLogo className="w-8 h-8 shrink-0 drop-shadow-xs" />
            <span className="text-lg font-black tracking-tight text-slate-900">
              PairUp<span className="text-pink-600">Today</span>
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-2.5">
          <LanguageSwitcher variant="compact" />
          <Link
            to="/home"
            className="text-xs font-bold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-full hover:bg-slate-100"
          >
            Explore Companions
          </Link>
          <Link
            to="/my-bookings"
            className="text-xs font-bold text-pink-600 bg-pink-50 px-3 py-1.5 rounded-full hover:bg-pink-100"
          >
            My Bookings
          </Link>
        </div>
      </header>

      {/* Main Profile Grid */}
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Photos & Details (2 Cols) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Profile Photo */}
            <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-xs">
              <div className="relative aspect-16/10 overflow-hidden bg-slate-100">
                <img
                  src={companion.image}
                  alt={companion.name}
                  className="w-full h-full object-cover"
                />

                <div className="absolute top-4 right-4 flex items-center gap-2">
                  {!isDemoCompanionProfile(companion) && companion.isVerified && (
                    <div className="px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-md">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      <span>Govt ID Verified</span>
                    </div>
                  )}
                </div>

                <div className="absolute bottom-4 left-4 px-3.5 py-1.5 bg-white/95 backdrop-blur-md rounded-2xl text-slate-900 font-mono font-black text-sm shadow-md">
                  ₹{hourlyRate.toLocaleString('en-IN')}/hour
                </div>
              </div>

              {/* Gallery Thumbnails */}
              {companion.gallery && companion.gallery.length > 1 && (
                <div className="p-4 flex gap-2.5 overflow-x-auto border-t border-slate-100">
                  {companion.gallery.map((img, i) => (
                    <div
                      key={i}
                      className="w-16 h-16 rounded-xl overflow-hidden border border-slate-200 shrink-0"
                    >
                      <img src={img} alt={`Gallery ${i}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Profile Bio & Details Card */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                    {companion.name}
                  </h1>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    <span>{companion.city}, {companion.state || 'India'}</span>
                    <span>•</span>
                    <span className="capitalize">{companion.gender}</span>
                    <span>•</span>
                    <span>Age {companion.age || 24}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1 text-sm font-bold text-amber-500 bg-amber-50 px-2.5 py-1 rounded-xl border border-amber-200">
                  <Star className="w-4 h-4 fill-current" />
                  <span>{companion.rating || 4.9}</span>
                  <span className="text-slate-400 font-normal text-xs">({companion.reviewsCount || 40})</span>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">About Me</h3>
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                  {companion.about || companion.bio}
                </p>
              </div>

              {/* Supported Occasions */}
              {companion.supportedOccasions && companion.supportedOccasions.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Supported Occasions & Services
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {companion.supportedOccasions.map((occ, i) => (
                      <span
                        key={i}
                        className="text-xs font-semibold px-3 py-1 rounded-xl bg-pink-50 text-pink-700 border border-pink-200"
                      >
                        {occ}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Safety Badges */}
              <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-200 text-xs text-emerald-950 flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
                <div className="space-y-0.5">
                  <strong className="block text-xs font-black">100% Background Checked</strong>
                  <p className="text-[11px] text-emerald-800">
                    Government identity, police clearance, and in-person interviews verified prior to listing approval.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Instant Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-md sticky top-24 space-y-5">
              <div className="border-b border-slate-100 pb-4">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                  Book This Companion
                </span>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-2xl font-black text-slate-900 font-mono">
                    ₹{hourlyRate.toLocaleString('en-IN')}
                  </span>
                  <span className="text-xs text-slate-500">per hour</span>
                </div>
              </div>

              <div className="space-y-3.5 text-xs">
                {/* Date Selection */}
                <div>
                  <label className="block font-bold text-slate-800 mb-1">Select Date</label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={bookingDate}
                    onChange={(e) => setBookingDate(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-pink-500"
                  />
                </div>

                {/* Time Selection */}
                <div>
                  <label className="block font-bold text-slate-800 mb-1">Meeting Start Time</label>
                  <input
                    type="time"
                    value={bookingTime}
                    onChange={(e) => setBookingTime(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-pink-500"
                  />
                </div>

                {/* Duration */}
                <div>
                  <label className="block font-bold text-slate-800 mb-1">
                    Duration: {durationHours} Hours
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={12}
                    value={durationHours}
                    onChange={(e) => setDurationHours(Number(e.target.value))}
                    className="w-full accent-pink-600 cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                    <span>1 hour</span>
                    <span>6 hours</span>
                    <span>12 hours</span>
                  </div>
                </div>

                {/* Occasion / Service */}
                <div>
                  <label className="block font-bold text-slate-800 mb-1">Occasion / Service</label>
                  <select
                    value={selectedOccasion}
                    onChange={(e) => setSelectedOccasion(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-pink-500 bg-white"
                  >
                    {(companion.supportedOccasions || ['Dinner Date', 'Wedding Guest', 'Cafe Meet']).map(
                      (occ, idx) => (
                        <option key={idx} value={occ}>
                          {occ}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* Meeting Location */}
                <div>
                  <BookingMeetingLocationPicker
                    initialLocationName={meetingLocation}
                    onLocationConfirmed={(loc: StructuredLocation) => {
                      setMeetingLocation(loc.formatted_address);
                      setMeetingLocationDetails(loc);
                      setBookingError(null);
                    }}
                  />
                  {bookingError && (
                    <p className="mt-1.5 text-xs font-bold text-rose-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{bookingError}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Price Calculation Summary */}
              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-600">
                  <span>₹{hourlyRate} × {durationHours} hrs</span>
                  <span className="font-mono font-bold text-slate-900">₹{computedTotal.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Trust & Safety Fee</span>
                  <span className="font-bold text-emerald-700">₹0 (Free)</span>
                </div>
                <div className="pt-2 border-t border-slate-200 flex justify-between font-black text-sm text-slate-900">
                  <span>Total Payable</span>
                  <span className="font-mono text-pink-600">₹{computedTotal.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {/* Booking Submit Action */}
              {isDemoCompanionProfile(companion) && (
                <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-300 text-amber-950 text-xs space-y-1.5">
                  <strong className="block font-black uppercase tracking-wider text-amber-900 text-xs">
                    Not available on this time / date
                  </strong>
                  <strong className="block font-bold uppercase tracking-wider text-amber-800 text-[11px]">
                    Not Available for This Booking Time
                  </strong>
                  <p className="font-semibold leading-relaxed">
                    This companion is a sample profile and cannot accept real bookings. Please choose an available verified companion.
                  </p>
                  <p className="text-[11px] text-amber-800">
                    {getDemoCompanionBookingMessage(language)}
                  </p>
                  <Link
                    to="/home"
                    className="mt-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-amber-900 text-white font-bold text-[11px] hover:bg-slate-900 transition-colors"
                  >
                    <span>Return to Real Companion List</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              )}
              <button
                type="button"
                onClick={handleStartBooking}
                className={`w-full py-3.5 font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer ${
                  isDemoCompanionProfile(companion)
                    ? 'bg-slate-300 text-slate-700 hover:bg-slate-400'
                    : 'bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white'
                }`}
              >
                <span>{isDemoCompanionProfile(companion) ? 'Not available on this time / date' : 'Proceed to Confirmation'}</span>
                <ChevronRight className="w-4 h-4" />
              </button>

              <p className="text-[10px] text-center text-slate-400">
                🔒 You will receive a unique Meeting Completion OTP code upon confirmation.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Sample Companion Non-Bookable Modal */}
      {showSampleNotAvailableModal && (
        <div
          id="sample-companion-not-available-modal"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setShowSampleNotAvailableModal(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full p-6 border border-amber-200 shadow-2xl space-y-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
              <AlertCircle className="w-7 h-7" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900">
              Not available on this time / date
            </h3>
            <p className="text-xs font-bold text-amber-800">
              Not Available for This Booking Time
            </p>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              This companion is a sample profile and cannot accept real bookings. Please choose an available verified companion.
            </p>
            <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-2.5">
              Not available on this time / date. Not Available for This Booking Time. Please choose an available verified companion.
            </p>
            <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
              <Link
                to="/home"
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold text-xs shadow-md transition-all"
              >
                Return to Companion List
              </Link>
              <button
                type="button"
                onClick={() => setShowSampleNotAvailableModal(false)}
                className="py-3 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Confirmation Modal */}
      {activeBooking && (
        <BookingConfirmationModal
          booking={activeBooking}
          onClose={() => setActiveBooking(null)}
          onSuccess={() => {
            setActiveBooking(null);
            navigate('/my-bookings');
          }}
        />
      )}

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-slate-400 border-t border-slate-200 mt-10 bg-white">
        PairUpToday Companion Host Details • Verified Cloud Firestore Profile
      </footer>
    </div>
  );
};

export default CompanionDetailPage;
